import type { PrimeWorkspaceFn } from '@/features/iam'
import { collection } from '@/infra/collection'
import { type MessageBankApi } from '@/features/messaging/api/messaging-api'
import { type MessageBankResource } from '@/features/messaging/api/resources/message-bank'
import { MessageBank, type MessageBankProps } from './MessageBank'
import { mapMessageBankReplyResourceToProps } from './message-bank-mappers'
import { maybeParseDate } from '@/utils/date-util'
import { Id } from '@/infra/id'

/**
 * Message banks: canned replies a workspace offers in the composer.
 */
export class MessageBankStore {
  /**
   * Collection of message banks.
   */
  readonly messageBanks = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new MessageBank(),
  })

  constructor(
    private readonly _api: MessageBankApi,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
  ) {}

  /**
   * Gets the loaded message bank for a workspace, if any.
   */
  getForWorkspace(workspaceId: Id) {
    return (
      this.messageBanks.find((mb) => mb.workspace.id === workspaceId) ?? null
    )
  }

  /**
   * Fetches the workspace's message bank.
   */
  async fetchWorkspaceMessageBank(workspaceId: Id, signal?: AbortSignal) {
    const resource = await this._api.getForWorkspace(
      Id.assert(workspaceId),
      signal,
    )
    return this.messageBanks.set(this._mapMessageBankResourceToProps(resource))
  }

  /**
   * Fetches the replies of a message bank.
   */
  async fetchMessageBankReplies(messageBankId: Id, signal?: AbortSignal) {
    const messageBank = this.messageBanks.get(messageBankId)
    const replies = await this._api
      .listReplies(Id.assert(messageBankId), signal)
      .then((data) => data.data)

    if (messageBank && replies) {
      return messageBank.replies.set(
        replies.map(mapMessageBankReplyResourceToProps),
      )
    }

    return []
  }

  private _mapMessageBankResourceToProps(
    resource: MessageBankResource,
  ): Partial<MessageBankProps> {
    return {
      id: resource.id,
      workspace: this._primeWorkspace({ id: resource.workspace_id }),
      dateCreated: maybeParseDate(resource.date_created),
      dateModified: maybeParseDate(resource.date_modified),
    }
  }
}
